import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdArrowBack, MdSave, MdImage, MdInventory2, MdDesignServices } from 'react-icons/md';
import { inventarioAPI } from '../services/api';
import { useModal } from '../context/ModalContext';
import { useConfig } from '../context/ConfigContext';

const Container = styled.div`width: 100%; max-width: 100%;`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  width: 40px; height: 40px;
  display: flex; align-items: center; justify-content: center;
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  color: ${p => p.theme.colors.textPrimary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  svg { font-size: 1.5rem; }
  &:hover { border-color: ${p => p.theme.colors.primary}; color: ${p => p.theme.colors.primary}; }
`;

const Title = styled.h1`
  font-size: ${p => p.theme.fontSizes.xxxl};
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
  display: flex; align-items: center; gap: 10px;
  svg { color: ${p => p.theme.colors.primary}; }
`;

const FormCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${p => p.theme.spacing.xl};

  @media (max-width: 760px) { grid-template-columns: 1fr; }
`;

const ImagePanel = styled.div``;

const ImageDrop = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border: 2px dashed ${p => p.has ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${p => p.theme.spacing.md};
  cursor: pointer;
  overflow: hidden;
  background: ${p => p.theme.colors.background};
  transition: all 0.2s;

  &:hover { border-color: ${p => p.theme.colors.primary}; }

  img { width: 100%; height: 100%; object-fit: cover; }

  svg { font-size: 3rem; color: ${p => p.theme.colors.textLight}; opacity: 0.5; }
  p { font-size: ${p => p.theme.fontSizes.sm}; color: ${p => p.theme.colors.textSecondary}; margin: 0; }
`;

const ImgHint = styled.p`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  margin: 8px 0 0;
  text-align: center;
`;

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.lg};
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: ${p => p.theme.fontWeights.medium};
  color: ${p => p.theme.colors.textPrimary};
  span.req { color: ${p => p.theme.colors.danger}; }
`;

const Input = styled.input`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const Select = styled.select`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
`;

const TextArea = styled.textarea`
  padding: ${p => p.theme.spacing.md};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  background: ${p => p.theme.colors.background};
  color: ${p => p.theme.colors.textPrimary};
  font-family: ${p => p.theme.fonts.primary};
  min-height: 100px;
  resize: vertical;
  &:focus { outline: none; border-color: ${p => p.theme.colors.primary}; }
  &::placeholder { color: ${p => p.theme.colors.textLight}; }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${p => p.theme.spacing.md};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  justify-content: flex-end;
  margin-top: ${p => p.theme.spacing.xl};
  padding-top: ${p => p.theme.spacing.xl};
  border-top: 1px solid ${p => p.theme.colors.border};
`;

const Btn = styled.button`
  padding: ${p => p.theme.spacing.md} ${p => p.theme.spacing.xl};
  background: ${p => p.primary
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundLight};
  color: ${p => p.primary ? 'white' : p.theme.colors.textPrimary};
  border: 1px solid ${p => p.primary ? p.theme.colors.primary : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex; align-items: center; gap: 6px;
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:hover:not(:disabled) { transform: translateY(-1px); }
`;

const ErrMsg = styled.p`
  color: ${p => p.theme.colors.danger};
  font-size: ${p => p.theme.fontSizes.sm};
  padding: ${p => p.theme.spacing.md};
  background: ${p => p.theme.colors.danger}10;
  border-radius: ${p => p.theme.borderRadius.md};
  border-left: 3px solid ${p => p.theme.colors.danger};
  margin: 0;
`;

const NuevoInventarioItem = () => {
  const navigate = useNavigate();
  const { toast } = useModal();
  const { config } = useConfig();
  const imgRef = useRef(null);

  const [imgPreview, setImgPreview] = useState(null);
  const [imgFile, setImgFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const isProducto = config?.tipo_negocio !== 'servicios';

  const [form, setForm] = useState({
    nombre: '', descripcion: '', precio: '',
    sku: '', stock: '0', stock_minimo: '5',
    duracion_minutos: '', duracion_unidad: 'minutos', categoria: '',
  });

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImage = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setImgFile(file);
    const reader = new FileReader();
    reader.onload = e => setImgPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('tipo', isProducto ? 'producto' : 'servicio');
      fd.append('nombre', form.nombre.trim());
      if (form.descripcion) fd.append('descripcion', form.descripcion);
      if (form.precio) fd.append('precio', form.precio);
      if (form.categoria) fd.append('categoria', form.categoria);
      if (isProducto) {
        if (form.sku) fd.append('sku', form.sku);
        fd.append('stock', form.stock || '0');
        fd.append('stock_minimo', form.stock_minimo || '5');
      } else {
        if (form.duracion_minutos) fd.append('duracion_minutos', form.duracion_minutos);
        fd.append('duracion_unidad', form.duracion_unidad || 'minutos');
      }
      if (imgFile) fd.append('imagen', imgFile);
      await inventarioAPI.create(fd);
      toast(`${isProducto ? 'Producto' : 'Servicio'} creado correctamente`, 'success');
      navigate('/inventario');
    } catch (err) {
      setError(err.message || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <PageHeader>
        <BackButton onClick={() => navigate(-1)}><MdArrowBack /></BackButton>
        <Title>
          {isProducto ? <MdInventory2 /> : <MdDesignServices />}
          {isProducto ? 'Nuevo Producto' : 'Nuevo Servicio'}
        </Title>
      </PageHeader>

      <FormCard>
        <form onSubmit={handleSubmit}>
          <Grid>
            {/* Image panel */}
            <ImagePanel>
              <Label>Imagen</Label>
              <ImageDrop
                has={!!imgPreview}
                onClick={() => imgRef.current?.click()}
                onDragOver={e => e.preventDefault()}
                onDrop={e => { e.preventDefault(); handleImage(e.dataTransfer.files[0]); }}
              >
                {imgPreview
                  ? <img src={imgPreview} alt="preview" />
                  : (<><MdImage /><p>Sube una imagen</p></>)
                }
              </ImageDrop>
              <ImgHint>JPG, PNG, WebP — max. 10 MB</ImgHint>
              <input ref={imgRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={e => handleImage(e.target.files[0])} />
            </ImagePanel>

            {/* Fields */}
            <Fields>
              <FieldGroup>
                <Label>Nombre <span className="req">*</span></Label>
                <Input name="nombre" value={form.nombre} onChange={handleChange}
                  placeholder={isProducto ? 'Nombre del producto' : 'Nombre del servicio'} required />
              </FieldGroup>

              <FieldGroup>
                <Label>Descripcion</Label>
                <TextArea name="descripcion" value={form.descripcion} onChange={handleChange}
                  placeholder="Descripcion detallada..." />
              </FieldGroup>

              <Row>
                <FieldGroup>
                  <Label>Precio</Label>
                  <Input type="number" name="precio" value={form.precio} onChange={handleChange}
                    placeholder="0.00" min="0" step="0.01" />
                </FieldGroup>
                <FieldGroup>
                  <Label>Categoria</Label>
                  <Input name="categoria" value={form.categoria} onChange={handleChange}
                    placeholder="Ej. Electronica, Limpieza..." />
                </FieldGroup>
              </Row>

              {isProducto ? (
                <Row>
                  <FieldGroup>
                    <Label>SKU / Codigo</Label>
                    <Input name="sku" value={form.sku} onChange={handleChange} placeholder="ABC-001" />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Stock inicial</Label>
                    <Input type="number" name="stock" value={form.stock} onChange={handleChange}
                      min="0" placeholder="0" />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Stock minimo</Label>
                    <Input type="number" name="stock_minimo" value={form.stock_minimo} onChange={handleChange}
                      min="0" placeholder="5" />
                  </FieldGroup>
                </Row>
              ) : (
                <Row>
                  <FieldGroup>
                    <Label>Duracion</Label>
                    <Input type="number" name="duracion_minutos" value={form.duracion_minutos}
                      onChange={handleChange} min="1" placeholder="1" />
                  </FieldGroup>
                  <FieldGroup>
                    <Label>Unidad</Label>
                    <Select name="duracion_unidad" value={form.duracion_unidad} onChange={handleChange}>
                      <option value="minutos">Minutos</option>
                      <option value="horas">Horas</option>
                      <option value="dias">Dias</option>
                      <option value="semanas">Semanas</option>
                      <option value="meses">Meses</option>
                      <option value="años">Años</option>
                    </Select>
                  </FieldGroup>
                </Row>
              )}

              {error && <ErrMsg>{error}</ErrMsg>}
            </Fields>
          </Grid>

          <ButtonRow>
            <Btn type="button" onClick={() => navigate(-1)} disabled={submitting}>Cancelar</Btn>
            <Btn type="submit" primary disabled={submitting}>
              <MdSave /> {submitting ? 'Guardando...' : 'Guardar'}
            </Btn>
          </ButtonRow>
        </form>
      </FormCard>
    </Container>
  );
};

export default NuevoInventarioItem;
